import * as Sentry from '@sentry/react';
import { IModal } from '../../typings';
import { WHATS_NEW_VERSION } from '../../whats-new/version';
import { CONTENT_DOC_VERSION, ROOT_COMPNENT_ID } from '@r3f-component-data-structure';
import * as settings from '../settings';
import { IContentDocActionTypes, IUserActionTypes, } from '../store/actions';
import { getShowWhatsNewLocalStorageState, getSnapshotDictFromLStorage, RECONNECTION_TOAST_ID, removeExpiredSceneSnapshots } from '../utils';
import { ToastsData } from '../utils/toasts-data';
import { cdsClient } from './cds';
import { WEBSOCKET_ACTIONS } from './websocket';
import { zwClient } from './zapworks';
export let projectId = location.pathname.replace(/\//g, '');
let docsReadyResolve;
const docsReadyPromise = new Promise((resolve) => {
    docsReadyResolve = resolve;
});
export const connectToCds = async (store) => {
    // if no project id, re-direct to my.zap.works
    if (!projectId || projectId.length === 0) {
        window.location.href = settings.ZW_BASE_URL;
        return;
    }
    // TODO: use react router to handle callback
    try {
        await zwClient.checkForAuthorizationResponse();
        // 5% loading progress
        store.dispatch({
            type: IUserActionTypes.SET_PROJECT_LOADING_PROGRESS,
            payload: 5
        });
        const qs = new URLSearchParams(location.search);
        if (qs.get('error'))
            return; // TODO: display auth error
        if (qs.get('code')) {
            projectId = window.atob(qs.get('state') || '').replace('p', '');
            if (projectId === '') {
                console.error('Invalid project ID:', projectId);
                return;
            }
            // projectId = Buffer.from(qs.get('state') || '', 'base64').toString('ascii');
            history.replaceState({}, document.title, `/${projectId}/`); // change URL from /callback/?code=... to /123/
            await zwClient.makeTokenRequest(); // get access token
            // 20% loading progress
            store.dispatch({
                type: IUserActionTypes.SET_PROJECT_LOADING_PROGRESS,
                payload: 20
            });
            if (projectId) {
                store.dispatch({
                    type: IUserActionTypes.SET_PROJECT_INFO,
                    payload: { project: { id: projectId, title: '' } }
                });
            }
        }
        else {
            clearLocalStorageAuthKeys(); // Start a fresh new auth process
            zwClient.makeAuthorizationRequest('user:read account:read media:read media:write project:read project:write project:publish', window.btoa(`p${projectId}`)); // redirect the user to ZapWorks login
            return;
        }
    }
    catch (err) {
        store.dispatch({
            type: IUserActionTypes.SET_IS_USER_AUTHORIZED,
            payload: false,
        });
        store.dispatch({
            type: IUserActionTypes.ADD_TOAST,
            payload: ToastsData.AuthorizationError
        });
        store.dispatch({
            type: IUserActionTypes.SET_PROJECT_LOADING_FAILURE,
            payload: true
        });
        Sentry.captureException(err);
    }
    clearLocalStorageAuthKeys(); // Auth process is complete, clean local storage
    const accessToken = zwClient.getAccessToken();
    cdsClient.setAccessToken(accessToken);
    cdsClient.setProjectId(projectId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cdsClient.addEventListener('error', (ev) => {
        let errorPayload, sendErrorReport = true, openModal = false;
        if (ev.detail.errorType === 'config') {
            if (ev.detail.errorCode >= 500) {
                errorPayload = ToastsData.InternalServerError;
            }
            else if (ev.detail.code === 'duplicatingError') {
                store.dispatch({
                    type: IUserActionTypes.SET_PROJECT_DUPLICATING,
                    payload: true
                });
                return; // this will be handled in the waitForProjectDuplication function
            }
            else if (ev.detail.errorCode === 403) {
                errorPayload = IModal.authorizationDenied;
                sendErrorReport = false;
                openModal = true;
            }
            else if (ev.detail.errorCode === 401) {
                errorPayload = IModal.sessionExpired;
                sendErrorReport = false;
                openModal = true;
            }
            cdsClient.stopReconnecting();
        }
        if (openModal) {
            document.documentElement.style.filter = 'grayscale(0)';
            store.dispatch({
                type: IUserActionTypes.OPEN_MODAL,
                payload: errorPayload
            });
            store.dispatch({
                type: IUserActionTypes.SET_IS_USER_AUTHORIZED,
                payload: false,
            });
        }
        else {
            store.dispatch({
                type: IUserActionTypes.ADD_TOAST,
                payload: errorPayload || ToastsData.SocketConnectionError
            });
        }
        store.dispatch({
            type: IUserActionTypes.SET_PROJECT_LOADING_FAILURE,
            payload: true
        });
        if (sendErrorReport) {
            Sentry.withScope(scope => {
                scope.setExtra('eventDetail', ev.detail);
                Sentry.captureMessage('CDS client error');
            });
        }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cdsClient.addEventListener('ws:config', async (ev) => {
        document.title = `${ev.detail.project.title} | Zapworks Designer`;
        Sentry.setUser({ id: ev.detail.user.id });
        Sentry.setExtra('project', ev.detail.project.id);
        const projectFeatures = await zwClient.getProjectFeatures(ev.detail.project.id);
        store.dispatch({
            type: IUserActionTypes.SET_USER_ID,
            payload: { userId: ev.detail.user.id },
        });
        const project = { ...ev.detail.project };
        if (typeof projectFeatures.maxDesignerTargetImages !== 'undefined') {
            project.maxTargetImages = projectFeatures.maxDesignerTargetImages;
        }
        if (typeof projectFeatures.canUseLmsIntegration !== 'undefined') {
            project.canUseLmsIntegration = projectFeatures.canUseLmsIntegration;
        }
        if (typeof projectFeatures.uarSizeLimit !== 'undefined') {
            project.sizeLimit = projectFeatures.uarSizeLimit;
        }
        store.dispatch({
            type: IUserActionTypes.SET_PROJECT_INFO,
            payload: { project },
        });
    });
    cdsClient.addEventListener('ws:open', async () => {
        // Re-enable user document interactions
        document.documentElement.style.filter = 'grayscale(0)';
        document.documentElement.style.pointerEvents = 'auto';
        store.dispatch({
            type: IUserActionTypes.SET_SOCKET_CONNECTED,
            payload: true
        });
        store.dispatch({
            type: IUserActionTypes.REMOVE_TOAST,
            payload: RECONNECTION_TOAST_ID
        });
    });
    cdsClient.addEventListener('ws:close', async () => {
        // Disable user document interactions while socket not connected
        document.documentElement.style.filter = 'grayscale(0.8)';
        document.documentElement.style.pointerEvents = 'none';
        store.dispatch({
            type: IUserActionTypes.SET_SOCKET_CONNECTED,
            payload: false
        });
        if (cdsClient.automergeDocsFound) {
            store.dispatch({
                type: IUserActionTypes.ADD_TOAST,
                payload: { ...ToastsData.SocketReconnecting, id: RECONNECTION_TOAST_ID, timeout: 0 }
            });
        }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cdsClient.addEventListener('syncdoc:ready', async (ev) => {
        await syncDocReadyHandler(store, ev.detail);
        docsReadyResolve();
    });
    const req = await fetch(`${settings.CDS_URL}${projectId}`, {
        headers: {
            'X-Authorization': `Basic ${accessToken}`,
            'ZW-Environment': settings.ZW_ENV
        }
    });
    if (req.status === 404) {
        const { isProjectDuplicating } = store.getState().userReducer;
        if (!isProjectDuplicating) {
            store.dispatch({
                type: IUserActionTypes.OPEN_MODAL,
                payload: IModal.template
            });
        }
        return cdsClient;
    }
    else if (req.status === 403) {
        store.dispatch({
            type: IUserActionTypes.SET_IS_USER_AUTHORIZED,
            payload: false,
        });
        store.dispatch({
            type: IUserActionTypes.OPEN_MODAL,
            payload: IModal.authorizationDenied
        });
        return;
    }
    else if (req.status !== 200) {
        // TODO: display a pretty user message
        throw 'Unable to get project';
    }
    // Automerge docs exist, start the websocket
    const response = await req.json();
    // response.previousVersion < response.docs.content.version --> 'migrated'
    const contentDoc = response.content;
    const contentDocVersion = contentDoc?.version;
    const sceneIds = contentDoc?.componentsById[contentDoc?.rootComponentId]?.children;
    cdsClient.automergeDocsFound = true;
    if (contentDocVersion !== CONTENT_DOC_VERSION) {
        store.dispatch({
            type: IUserActionTypes.ADD_TOAST,
            payload: ToastsData.MismatchedVersionError
        });
        return;
    }
    if (sceneIds?.length === 0) {
        const { isProjectDuplicating } = store.getState().userReducer;
        if (!isProjectDuplicating) {
            store.dispatch({
                type: IUserActionTypes.OPEN_MODAL,
                payload: IModal.template
            });
        }
    }
    store.dispatch({
        type: IUserActionTypes.SET_IS_USER_AUTHORIZED,
        payload: true,
    });
    await createWebSocket(store);
    return cdsClient;
};
export const forkProject = async (source, destination) => {
    await cdsClient.forkProject(source, destination);
};
export const createWebSocket = async (store) => {
    const socket = await cdsClient.connectToWebSocket();
    if ('error' in socket && 'code' in socket)
        return socket;
    socket.addMessageListener(WEBSOCKET_ACTIONS.UpdateUsers, async (users) => {
        await docsReadyPromise;
        store.dispatch({
            type: IUserActionTypes.UPDATE_USERS,
            payload: { users }
        });
    });
    store.dispatch({
        type: IUserActionTypes.SET_PROJECT_LOADING_PROGRESS,
        payload: 80
    });
    await docsReadyPromise;
    store.dispatch({
        type: IUserActionTypes.SET_PROJECT_LOADING_PROGRESS,
        payload: 100
    });
    const showWhatsNewModal = getShowWhatsNewLocalStorageState(WHATS_NEW_VERSION);
    const scenes = store.getState().contentReducer.contentDoc.componentsById[ROOT_COMPNENT_ID].children;
    if (showWhatsNewModal && !!scenes.length && cdsClient.automergeDocsFound)
        setTimeout(() => {
            store.dispatch({
                type: IUserActionTypes.OPEN_MODAL,
                payload: IModal.whatsNew
            });
        }, 1000);
    return socket;
};
const syncDocReadyHandler = async (store, syncDoc) => {
    removeExpiredSceneSnapshots(); // remove any expired snapshots (from other projects too)
    syncDoc.addEventListener('sync', async () => {
        await syncDoc.waitForDoc();
        const contentDoc = syncDoc.getDoc();
        const { selectedEntityIds } = store.getState().userReducer;
        const componentIds = Object.keys(contentDoc.componentsById);
        const filtSelEntityIds = selectedEntityIds?.filter(id => componentIds.includes(id));
        store.dispatch({
            type: IUserActionTypes.SET_SELECTED_ENTIY_IDS,
            payload: filtSelEntityIds || [],
        });
        store.dispatch({
            type: IContentDocActionTypes.CHANGE_CN_DOC_REDUX,
            contentDoc,
        });
    });
    const zoom = parseFloat(localStorage.getItem(`${projectId}_zoom_level`) || '');
    if (typeof zoom === 'number' && !isNaN(zoom)) {
        store.dispatch({
            type: IUserActionTypes.CHANGE_ZOOM_LEVEL,
            payload: zoom
        });
    }
    await syncDoc.waitForDoc();
    const doc = syncDoc.getDoc();
    if (typeof doc.componentsById === 'undefined') {
        Sentry.captureMessage("Content doc's componentsById is undefined", {
            extra: { contentDoc: JSON.parse(JSON.stringify(doc)) }
        });
    }
    store.dispatch({
        type: IContentDocActionTypes.CHANGE_CN_DOC_REDUX,
        contentDoc: doc,
    });
    store.dispatch({
        type: IUserActionTypes.SET_SCENE_SNAPSHOTS,
        payload: getSnapshotDictFromLStorage(localStorage, projectId),
    });
};
function clearLocalStorageAuthKeys() {
    for (const key in localStorage) {
        if (key.includes('appauth_')) {
            localStorage.removeItem(key);
        }
    }
}
export function waitForProjectDuplication(store) {
    return new Promise((resolve) => {
        const interval = setInterval(async () => {
            const res = await createWebSocket(store);
            if ('code' in res && res.code === 'duplicatingError')
                return; // still duplicating
            store.dispatch({
                type: IUserActionTypes.SET_PROJECT_DUPLICATING,
                payload: false
            });
            clearInterval(interval);
            resolve();
        }, 1500);
    });
}
